import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import {
  ComposedChart,
  Bar,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { lightPalette } from '../../../theme';
import { getCreditsInfo } from '../../../services/adminDashboard';

const CreditsBarChart = () => {
  const [chartData, setChartData] = useState([
    {
      name: 'Used',
      UsedBankStatement: 0,
      UsedCreditCardStatement: 0,
      UsedInvoice: 0,
      UsedDirectExpenses: 0,
      UsedIndirectExpenses: 0,
    },
    {
      name: 'Remaining',
      Remaining: 0,
    },
  ]);
  const [loading, setLoading] = useState(true);

  const tooltipFormatter = (value, name) => {
    let newValue;
    if (name === 'Remaining') {
      newValue = `${value} Credits Remaining`;
      return [newValue];
    }
    return [value, name];
  };

  const renderLegend = (props) => {
    const { payload } = props;

    const renderCustomLabel = (value) => {
      switch (value) {
        case 'UsedBankStatement':
          return 'Bank Statement';
        case 'UsedCreditCardStatement':
          return 'Credit Card Statement';
        case 'UsedInvoice':
          return 'Invoice';
        case 'UsedDirectExpenses':
          return 'Direct Expenses';
        case 'UsedIndirectExpenses':
          return 'Indirect Expenses';
        case 'Remaining':
          return 'Remaining';
        default:
          return value;
      }
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
        }}
      >
        {payload?.map((entry, index) => (
          <Box
            key={`${index + 1}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: '10px',
                height: '10px',
                marginRight: '8px',
                backgroundColor: entry?.color || 'black',
              }}
            />
            <Box
              sx={{
                color: lightPalette.deepAmethyst.semiDark,
              }}
              key={`${index + 1}`}
            >
              {renderCustomLabel(entry.value)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const getCreditsData = async () => {
    const response = await getCreditsInfo();
    if (response && Object.keys(response?.data?.stages)?.length > 0) {
      const finalData = response.data.stages;
      setChartData([
        {
          name: 'Used',
          UsedBankStatement: finalData?.used_credits?.bank_statements || 0,
          UsedCreditCardStatement:
            finalData?.used_credits?.credit_card_statements || 0,
          UsedInvoice: finalData?.used_credits?.sales || 0,
          UsedDirectExpenses: finalData?.used_credits?.invoices || 0,
          UsedIndirectExpenses: finalData?.used_credits?.receipts || 0,
        },
        {
          name: 'Remaining',
          Remaining: finalData?.remaining_credits || 0,
        },
      ]);
      setLoading(false);
    } else {
      setChartData([]);
      setLoading(true);
    }
  };

  useEffect(() => {
    getCreditsData();
  }, []);
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <ResponsiveContainer
      width="90%"
      height="100%"
      style={{
        margin: 'auto',
      }}
    >
      <ComposedChart data={chartData} width={50} barCategoryGap="20%">
        <CartesianGrid
          stroke="rgba(46, 37, 89, 0.1)"
          strokeDasharray="none"
          vertical={false}
          horizontal
        />
        <XAxis dataKey="name" tickLine={false} axisLine={false} />
        <Tooltip
          formatter={tooltipFormatter}
          contentStyle={{
            backgroundColor: 'rgba(255, 255, 255, 1)',
            color: 'rgba(0, 0, 0, 0.6)',
            boxShadow: '0px 0px 11px 4px rgba(0, 0, 0, 0.08)',
            borderRadius: '5px',
            padding: '10px',
          }}
          labelStyle={{ display: 'none' }}
          itemStyle={{ color: 'rgba(0, 0, 0, 0.6)' }}
        />
        <Legend content={renderLegend} align="left" />

        <Bar
          stackId="a"
          dataKey="UsedBankStatement"
          fill="rgba(159, 119, 235, 1)"
        />
        <Bar
          stackId="a"
          dataKey="UsedCreditCardStatement"
          fill="rgba(185, 150, 252, 1)"
          barSize={20}
        />
        <Bar
          stackId="a"
          dataKey="UsedInvoice"
          fill="rgba(210, 187, 254, 1)"
          barSize={20}
        />
        <Bar
          stackId="a"
          dataKey="UsedDirectExpenses"
          fill="rgba(225, 209, 255, 1)"
          barSize={20}
        />
        <Bar
          stackId="a"
          dataKey="UsedIndirectExpenses"
          fill="rgba(237, 227, 255, 1)"
          barSize={20}
        />
        <Bar
          stackId="a"
          dataKey="Remaining"
          fill="rgba(237, 227, 255, 1)"
          barSize={20}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

CreditsBarChart.defaultProps = {
  payload: [],
};

CreditsBarChart.propTypes = {
  payload: PropTypes.arrayOf(),
};

export default CreditsBarChart;
